import { centerNow } from './clock'

/*
 * Formatting for the console (design/network-console/proto/src/lib/format.ts).
 * The caller renders numbers in mono with tabular figures. Times are UTC, 24 h, dates ISO
 * (owner SYS-09, 23.10; system E-10 DREQ-02, 04-typography «Console, status page and logs»,
 * AUDIT CN-06): feeds and tables with seconds and no zone ("14:05:09"), a time on its own with
 * the zone ("2026-10-06 11:15 UTC"). The day rows of the journal stay "6 Oct 2026" (NG-10).
 */

export const ms = (v: number | null | undefined) => (v == null ? '—' : v >= 1000 ? `${(v / 1000).toFixed(2)} s` : `${Math.round(v)} ms`)

/** Sub-millisecond steps of a frame trace: "0.4 ms", "41.6 ms". */
export const ms1 = (v: number | null | undefined) => (v == null ? '—' : Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(2)} s` : `${v.toFixed(1)} ms`)

export const pct = (v: number | null | undefined, digits = 1) => (v == null ? '—' : `${v.toFixed(digits)}%`)

export function bps(v: number | null | undefined): string {
  if (v == null) return '—'
  if (v >= 1e9) return `${(v / 1e9).toFixed(2)} Gb/s`
  if (v >= 1e6) return `${(v / 1e6).toFixed(v >= 1e8 ? 0 : 1)} Mb/s`
  if (v >= 1e3) return `${Math.round(v / 1e3)} kb/s`
  return `${v} b/s`
}

const pad = (n: number) => String(n).padStart(2, '0')

const parse = (iso?: string | number | null): Date | null => {
  if (iso == null || iso === '') return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : d
}
const ymd = (d: Date) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`
const hm = (d: Date) => `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`
const hms = (d: Date) => `${hm(d)}:${pad(d.getUTCSeconds())}`

/** HH:MM:SS UTC, no zone: the time column of a feed or a table. */
export function clock(iso?: string | number | null): string {
  const d = parse(iso)
  return d ? hms(d) : '—'
}

/** "14:05:09 UTC": a time on its own, with the zone. */
export function clockUTC(iso?: string | number | null): string {
  const d = parse(iso)
  return d ? `${hms(d)} UTC` : '—'
}

/** "2026-10-06": an ISO date in UTC. */
export function isoDate(iso?: string | number | null): string {
  const d = parse(iso)
  return d ? ymd(d) : '—'
}

/** "2026-10-06 11:15 UTC": a moment on its own (an incident, a deadline). */
export function stampMin(iso?: string | number | null): string {
  const d = parse(iso)
  return d ? `${ymd(d)} ${hm(d)} UTC` : '—'
}

/** "2026-10-06 11:15:09 UTC": a moment in a log or a table, with seconds. */
export function stamp(iso?: string | number | null): string {
  const d = parse(iso)
  return d ? `${ymd(d)} ${hms(d)} UTC` : '—'
}

/** "2026-10-06 11:15:09": the time column of a log whose rows span days (the column says UTC). */
export function dateClock(iso?: string | number | null): string {
  const d = parse(iso)
  return d ? `${ymd(d)} ${hms(d)}` : '—'
}

/** "14:05 UTC" today (UTC), "2026-10-12 14:05 UTC" on another day. */
export function stampShort(iso: string | number, now = Date.now()): string {
  const d = parse(iso)
  if (!d) return '—'
  return ymd(d) === ymd(new Date(now)) ? `${hm(d)} UTC` : `${ymd(d)} ${hm(d)} UTC`
}

/** "now", "12 s ago", "4 min ago", "3 h ago", "2 d ago" — on the center's clock. */
export function ago(iso?: string | null, now = centerNow()): string {
  if (!iso) return '—'
  const s = Math.max(0, Math.round((now - Date.parse(iso)) / 1000))
  if (s < 5) return 'now'
  if (s < 60) return `${s} s ago`
  if (s < 3600) return `${Math.floor(s / 60)} min ago`
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`
  return `${Math.floor(s / 86400)} d ago`
}

/** "1 h 58 min", "4 min 12 s", "9 s". */
export function dur(fromIso?: string | null, now = centerNow()): string {
  if (!fromIso) return '—'
  const s = Math.max(0, Math.round((now - Date.parse(fromIso)) / 1000))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  return h ? `${h} h ${m} min` : m ? `${m} min ${s % 60} s` : `${s} s`
}

/** "a1b2 c3d4 … 9f0e": the first 8 and the last 4 hex digits of an SPKI hash. */
export const spkiShort = (hex: string) => `${hex.slice(0, 4)} ${hex.slice(4, 8)} … ${hex.slice(-4)}`

/** "a1b2 c3d4 e5f6 …": the whole hash in groups of four, for reading aloud. */
export const spkiGroups = (hex: string) => hex.replace(/(.{4})(?=.)/g, '$1 ')

/** A 64-bit sequence number with thin spaces every 3 digits: 1 204 880. */
export const seq = (n: number | null | undefined) => (n == null ? '—' : n.toLocaleString('en-US').replace(/,/g, ' '))

export const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`
